'use strict';
__webpack_public_path__ = __webpack_public_path__ + 'js/'; // Webpack public path for dynamic imports

import 'lazysizes';
import {
  customEvent,
  body,
  smoothScrollToAnchor
} from '@jspartials/general-utils';
import {
  classes,
  addClassToElement,
  removeClassToElement,
  hasClassElement
} from '@jspartials/class-list-utils';
import { listenProductTiles } from '@jscomponents/product-tile'; // (100kb)
import {
  gtmCustomEvent,
  gtmLaunchAddToCartProduct,
  gtmLaunchRemoveFromCartProduct,
  gtmLaunchPageView,
  gtmLaunchAddProductToWishlist,
  gtmLaunchRemoveProductFromWishlist
} from '@jspartials/gtm-utils';
import {
  notifyAfterAddedToCartSuccessfullyInProductTile,
  updateNotificationAfterAddedToCartSuccessfullyInProductDetails
} from '@jscomponents/notification';
import {
  listenMiniCartIcon,
  getMiniCartTotalQuantity
} from '@jscomponents/mini-cart';
import { listenImagesCarousel } from '@jspages/search/imagesCarouselTiles';

require('./components/header.js'); // (20kb)
require('./components/hellobar.js'); // (3kb)
require('./components/searcher.js'); // (24kb)
require('./components/sidebar.js'); // (10kb)
require('./components/spinner.js'); // (2kb)
require('./components/video-lazy.js'); // (2kb)
require('./components/virtual-try-on.js'); // Move the logic outside of the file to decide if it should be rendered (20kb)
require('./components/site-selector.js'); // (2kb)
require('./components/pick-up-point-reset.js'); // (1kb)
require('./gtm/gtmEventsDataLayer.js'); // 16kb
require('./gtm/gtmCustomSessionId.js'); // 1kb

// Product tiles: the secondary image is only shown on hover (mouse devices).
// On touch devices it's never visible, so lazysizes doesn't load it (it
// removes the lazyload class anyway, so it doesn't retry). It's registered
// here because lazysizes starts in a timeout after this module runs
const mouseMediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

document.addEventListener('lazybeforeunveil', (event) => {
  if (
    !mouseMediaQuery.matches &&
    event.target.closest('.js-product-tile__image--secondary')
  ) {
    event.preventDefault();
  }
});

// Variables
const distanceToActivateHeaderHidden = 20;
let distanceOfCollisionBetweenHeaderAndFiltersInfoBar = 0;
let lastScrollTop = 0;

// Classes
const HEADER_HIDDEN_CLASSNAME = 'is-header-hidden';
const FILTERS_INFO_BAR_CLASSNAME = 'js-filters__info-bar';
const FILTERS_BAR_IS_FLOATING_CLASSNAME = 'filters-bar-is-floating';

// Selectors
const FILTERS_INFO_BAR_SELECTOR = `.${FILTERS_INFO_BAR_CLASSNAME}`;

// Elements
const filtersInfoBar =
  document.querySelector(FILTERS_INFO_BAR_SELECTOR) || null;

// Functions

function setHeaderHidden() {
  let scrollTop = document.documentElement.scrollTop;

  if (Math.abs(lastScrollTop - scrollTop) <= distanceToActivateHeaderHidden) {
    return;
  }

  if (scrollTop > lastScrollTop && lastScrollTop > 0) {
    if (!hasClassElement(body, HEADER_HIDDEN_CLASSNAME)) {
      addClassToElement(body, HEADER_HIDDEN_CLASSNAME);
    }
  } else {
    if (hasClassElement(body, HEADER_HIDDEN_CLASSNAME)) {
      removeClassToElement(body, HEADER_HIDDEN_CLASSNAME);
    }
  }

  lastScrollTop = scrollTop;
}

function setFiltersInfoBarFixed() {
  if (filtersInfoBar) {
    const scrollTop = document.documentElement.scrollTop;
    const filtersInfoBarObject = filtersInfoBar.getBoundingClientRect();

    if (filtersInfoBarObject.bottom < 0) {
      if (!hasClassElement(filtersInfoBar, classes.is.fixed)) {
        distanceOfCollisionBetweenHeaderAndFiltersInfoBar = Math.abs(
          Math.abs(filtersInfoBarObject.top) - scrollTop
        );
        addClassToElement(filtersInfoBar, classes.is.fixed);
        addClassToElement(body, FILTERS_BAR_IS_FLOATING_CLASSNAME);
      }
    } else {
      if (scrollTop <= distanceOfCollisionBetweenHeaderAndFiltersInfoBar) {
        if (hasClassElement(filtersInfoBar, classes.is.fixed)) {
          removeClassToElement(filtersInfoBar, classes.is.fixed);
          removeClassToElement(body, FILTERS_BAR_IS_FLOATING_CLASSNAME);
        }
      }
    }
  }
}

// Init
document.addEventListener('DOMContentLoaded', function () {
  // Launch GTM page view
  gtmLaunchPageView(gtmDataForViewPage);

  // Init product tiles
  listenProductTiles();

  // Init mini cart
  listenMiniCartIcon();

  // Get mini cart total quantity
  getMiniCartTotalQuantity();

  // Apply smooth scroll to anchor links
  smoothScrollToAnchor();

  // Check if there is a countdown
  const unfilteredCountdownElements =
    document.querySelectorAll('.js-countdown');
  unfilteredCountdownElements.forEach((countdownElement) => {
    const countdownStart = Date.parse(countdownElement.dataset.start);
    const countdownEnd = Date.parse(countdownElement.dataset.end);
    const remainingTime = countdownEnd - Date.now();

    if (
      isNaN(countdownStart) ||
      isNaN(countdownEnd) ||
      countdownStart > countdownEnd ||
      countdownStart > Date.now() ||
      remainingTime <= 0
    ) {
      countdownElement.remove();
    }
  });

  const countdownElements = document.querySelectorAll('.js-countdown');
  if (countdownElements.length > 0) {
    import(/* webpackChunkName: "countdown" */ '@jscomponents/countdown').then(
      ({ initCountdowns }) => {
        initCountdowns();
      }
    );
  }

  // Init images carousel (used in collections landings and other pages)
  const imagesCarouselElements = document.querySelectorAll(
    '.js-images-carousel'
  );
  if (imagesCarouselElements.length > 0) {
    listenImagesCarousel();
  }
});

// When scroll...
window.addEventListener('scroll', () => {
  setHeaderHidden();
  setFiltersInfoBarFixed();
});

// Listen custom events

// When login and signup page is loaded...
document.addEventListener(customEvent.loginAndSignupLoaded, function () {
  import(
    /* webpackChunkName: "login-and-signup" */ '@jspages/account/login/login-and-signup'
  ).then(({ init }) => {
    init();
  });
});

// When login and signup page is loaded...
document.addEventListener(customEvent.qrGeneratorLoaded, function () {
  import(
    /* webpackChunkName: "qr-generator" */ '@jspages/account/qr-generator'
  ).then(({ initQrCode }) => {
    initQrCode();
  });
});

// News products tiles added to DOM
document.addEventListener(customEvent.listenProductTiles, (event) => {
  const details = event.detail;
  const gtmResetList = details?.gtmResetList || false;
  listenProductTiles(gtmResetList);
});

// Listen GTM custom events

// After add to cart...
document.addEventListener(gtmCustomEvent.afterAddToCart, (event) => {
  const details = event.detail;
  const productId = details?.productId || null;
  const quantity = details?.quantity || 1;
  const list = details?.list || '';

  gtmLaunchAddToCartProduct(productId, quantity, list);
});

// After remove from cart...
document.addEventListener(gtmCustomEvent.afterRemoveFromCart, (event) => {
  const details = event.detail;
  const productId = details?.productId || null;
  const quantity = details?.quantity || 1;
  const list = details?.list || '';

  gtmLaunchRemoveFromCartProduct(productId, quantity, list);
});

// After add to wishlist...
document.addEventListener(gtmCustomEvent.afterAddToWishlit, (event) => {
  const details = event.detail;
  const productId = details?.productId || null;

  gtmLaunchAddProductToWishlist(productId);
});

// After remove from wishlist...
document.addEventListener(gtmCustomEvent.afterRemoveFromWishlist, (event) => {
  const details = event.detail;
  const productId = details?.productId || null;

  gtmLaunchRemoveProductFromWishlist(productId);
});

// When cookies consent is updated...
window.addEventListener('CookiebotOnConsentReady', function () {
  document.dispatchEvent(new CustomEvent(customEvent.cookieBot.onConsentReady));
});

// Listen custom event when product tile is added to cart successfully
document.addEventListener(
  globalVariables.customEvent.productTileAddedToCartSuccessfully,
  (event) => {
    const details = event.detail;
    const productId = details?.productId || null;
    const errorMessage = details?.errorMessage || '';
    const gtmListName = details?.gtmListName || '';

    if (productId && !globalVariables.currentScreenIsCart) {
      if (gtmListName === 'recommendations-after-add-to-cart') {
        updateNotificationAfterAddedToCartSuccessfullyInProductDetails(
          productId
        );
      } else {
        notifyAfterAddedToCartSuccessfullyInProductTile(
          productId,
          errorMessage
        );
      }
    }
  }
);
